/**
 * @file sessionSignals.js
 * @description How a session ends, told the same way everywhere.
 *
 * Three bugs shared one cause — every surface decided on its own how to end
 * a session and what to say afterwards:
 *
 *   - A deliberate "Sign out" sent people to `/login?reason=session_expired`,
 *     so the person who chose to leave was told their session had run out.
 *   - The idle time-out sent them to `/?reason=session_expired`, where nothing
 *     reads the reason, and dropped the page they were on.
 *   - Idle time was counted per tab, so a background tab could time out and
 *     sign out the session somebody was actively using in another tab.
 *
 * This file is the one place that knows the reasons, builds the login URL,
 * and carries activity and sign-outs between tabs (localStorage + the
 * `storage` event, which every browser we support fires in OTHER tabs only).
 */
import { storage } from '../../utils/storage';

export const SIGN_OUT_REASONS = Object.freeze({
  // The person chose to leave (or left in another tab). Neutral copy.
  SIGNED_OUT: 'signed_out',
  // The session ran out on its own — idle time-out or a failed refresh.
  SESSION_EXPIRED: 'session_expired'
});

// Shared between tabs of this browser. Not identity-bearing: a timestamp and
// a reason, so they are safe in localStorage.
export const LAST_ACTIVITY_KEY = 'moksy_last_activity_at';
export const SIGN_OUT_SIGNAL_KEY = 'moksy_signed_out_signal';

// How long a just-written sign-out signal stands before a plain "signed out"
// may replace it. Long enough to cover the logout request that follows an
// idle expiry, short enough that a later deliberate sign-out is not labelled
// as an expiry.
const SIGNAL_HOLD_MS = 3000;

const local = () => {
  try { return typeof window !== 'undefined' ? window.localStorage : null; } catch { return null; }
};

/**
 * `/login?reason=…&next=…`. `next` is kept only when it is an in-app path and
 * not the login page itself — the same rule api/client.js applies on a 401.
 */
export const loginUrl = ({ reason = null, next = null } = {}) => {
  const params = new URLSearchParams();
  if (reason) params.set('reason', reason);
  if (typeof next === 'string' && next.startsWith('/') && !next.startsWith('//') && !next.startsWith('/login')) {
    params.set('next', next);
  }
  const qs = params.toString();
  return qs ? `/login?${qs}` : '/login';
};

export const currentPath = () => {
  try { return `${window.location.pathname}${window.location.search}`; } catch { return null; }
};

// ─── Activity shared across tabs ─────────────────────────────────────────

export const readSharedActivity = (store = local()) => {
  try {
    const n = Number(store?.getItem(LAST_ACTIVITY_KEY));
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch { return 0; }
};

/**
 * Record activity for every tab. Only ever moves forward, so a tab with an
 * older clock reading cannot wind back the time another tab just wrote.
 */
export const publishActivity = (at = Date.now(), store = local()) => {
  try {
    if (at > readSharedActivity(store)) store?.setItem(LAST_ACTIVITY_KEY, String(at));
  } catch { /* storage unavailable — this tab still counts its own activity */ }
};

// A shared timestamp further ahead of this tab's clock than this is not
// activity, it is a clock that moved (or a value written under a different
// one). Trusting it would stop every tab expiring until the clock caught up.
const CLOCK_TOLERANCE_MS = 5000;

/** The most recent activity in ANY tab, given this tab's own. */
export const latestActivity = (ownAt, store = local(), now = Date.now()) => {
  const shared = readSharedActivity(store);
  const usable = shared <= now + CLOCK_TOLERANCE_MS ? shared : 0;
  return Math.max(Number(ownAt) || 0, usable);
};

// ─── Sign-out shared across tabs ─────────────────────────────────────────

const ownerId = () => {
  try { const id = storage.get(storage.KEYS.USER_ID); return id ? String(id) : null; } catch { return null; }
};

export const parseSignOutSignal = (raw) => {
  if (!raw) return null;
  try {
    const s = JSON.parse(raw);
    if (!s || typeof s !== 'object' || !Object.values(SIGN_OUT_REASONS).includes(s.reason)) return null;
    return { reason: s.reason, at: Number(s.at) || 0, uid: s.uid ? String(s.uid) : null };
  } catch { return null; }
};

/**
 * Tell other tabs this session has ended. `keepRecent` leaves a signal written
 * in the last few seconds alone — the logout request that follows an idle
 * expiry must not relabel it "signed out".
 */
export const announceSignOut = (reason = SIGN_OUT_REASONS.SIGNED_OUT, { keepRecent = false, uid = ownerId(), now = Date.now(), store = local() } = {}) => {
  try {
    if (keepRecent) {
      const existing = parseSignOutSignal(store?.getItem(SIGN_OUT_SIGNAL_KEY));
      if (existing && now - existing.at < SIGNAL_HOLD_MS) return;
    }
    store?.setItem(SIGN_OUT_SIGNAL_KEY, JSON.stringify({ reason, at: now, uid }));
  } catch { /* storage unavailable */ }
};

// How long after a sign-out a route guard still reads it as the reason it is
// looking at nobody. Covers the logout request and the re-render that follows.
const GUARD_SIGNAL_MS = 10000;

/**
 * Where a route guard sends somebody it finds signed out.
 *
 * Every guard used to say `reason=auth_required` — "Please log in or create an
 * account to continue" — whatever had happened. So a person who pressed Sign
 * out on /profile was answered as if they had been refused entry (GC-092):
 * the context's `logout` cleared the user, the page re-rendered under its
 * guard, and the guard, knowing nothing, assumed a stranger. The sign-out
 * signal every logout path already writes (api/auth.js → announceSignOut) is
 * the fact the guard was missing. Read here, once, for every guard — so a
 * sign-out from ANY button, present or future, lands on "You are signed out".
 */
export const guardLoginUrl = ({ next = null, store = local(), now = Date.now() } = {}) => {
  let signal = null;
  try { signal = parseSignOutSignal(store?.getItem(SIGN_OUT_SIGNAL_KEY)); } catch { signal = null; }
  if (signal && now - signal.at >= 0 && now - signal.at < GUARD_SIGNAL_MS) {
    // A deliberate sign-out is not a request to come back to this page; an
    // expiry is, so it keeps `next`.
    return loginUrl({ reason: signal.reason, next: signal.reason === SIGN_OUT_REASONS.SESSION_EXPIRED ? next : null });
  }
  const params = new URLSearchParams({ reason: 'auth_required' });
  if (typeof next === 'string' && next.startsWith('/')) params.set('next', next);
  return `/login?${params.toString()}`;
};

/**
 * Does a sign-out from another tab apply to THIS tab? Not when both tabs know
 * whose session they hold and it is a different person (a detached tab).
 */
export const signalAppliesHere = (signal, ownUid = ownerId()) => {
  if (!signal) return false;
  if (signal.uid && ownUid && signal.uid !== ownUid) return false;
  return true;
};

/**
 * End the session from a user action or a time-out, then leave.
 *
 *   signOutAndLeave(logout)                                 → deliberate, neutral copy
 *   signOutAndLeave(logout, { reason: SESSION_EXPIRED, next }) → expiry, back to the page
 */
export const signOutAndLeave = async (logout, { reason = SIGN_OUT_REASONS.SIGNED_OUT, next = null, to = null } = {}) => {
  // Before the logout request: navigating away can cancel it, and the other
  // tabs must hear the right reason either way.
  announceSignOut(reason);
  try { await logout?.(); } catch { /* the context clears local state regardless */ }
  try { window.location.assign(to || loginUrl({ reason, next })); } catch { /* ignore */ }
};

/**
 * Another tab ended this session. Clear what this tab holds and go to the
 * login page with the reason, keeping the page so logging back in returns
 * here. Deliberately NOT the context logout: that would post /logout again and
 * re-announce to every tab.
 */
export const leaveAfterSignOutElsewhere = (signal) => {
  try { storage.clear(); } catch { /* ignore */ }
  try { window.location.assign(loginUrl({ reason: signal?.reason || SIGN_OUT_REASONS.SIGNED_OUT, next: currentPath() })); } catch { /* ignore */ }
};
